import { FC, useEffect, useState } from "react";
import {
  EuiBasicTableColumn,
  EuiButton,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiInMemoryTable, EuiSpacer,
  EuiText,
} from "@elastic/eui";
import { DateRangePicker } from "../../components/time/DateRangePicker";
import moment, { Moment } from "moment";
import { useInvoicesGetAllQuery } from "../../api/rtkQueryApi/InvoicesApi";
import { NumberToCurrency } from "../../utils/Formatters";
import { useGetBusinessesQuery } from "../../api/rtkQueryApi/BusinessesApi";
import { useGetUsersQuery } from "../../api/rtkQueryApi/UsersApi";
import { DownloadAsCsv } from "../../utils/DownloadAsCsv";
import { Invoice, InvoiceLine } from "../../api/entities/Invoice";

interface InvoiceData {
  issueDate: Moment | null;
  businessName: string;
  product: string;
  invoicesPaid: string;
  totalAmount: number;
  status: string;
}

const getProductName = (lines: InvoiceLine[]): string => {
  if (lines.length === 0) {
    return "";
  }
  return lines[0].title;
};

const getTotalInvoicesForBusiness = (
  businessId: string,
  invoices: Invoice[],
) => {
  return invoices.filter((i) => i.businessId === businessId).length;
};

export const AccountingInvoiceSummary: FC = () => {
  const [startDate, setStartDate] = useState<Moment>(moment().startOf("month"));
  const [endDate, setEndDate] = useState<Moment>(moment().endOf("month"));
  const [minDate] = useState<Moment>();
  const [maxDate] = useState<Moment>();

  const getInvoicesQuery = useInvoicesGetAllQuery();
  const businessesQuery = useGetBusinessesQuery();
  const usersQuery = useGetUsersQuery();

  const [filteredInvoices, setFilteredInvoices] = useState<InvoiceData[]>([]);

  useEffect(() => {
    if (getInvoicesQuery.data && businessesQuery.data && usersQuery.data) {
      {
        const filteredI = getInvoicesQuery.data.filter((i) =>
          moment(i.issueDate).isBetween(startDate, endDate),
        );
        const resultData: InvoiceData[] = [];

        filteredI.forEach((i) => {
          const business = businessesQuery.data!.find(
            (b) => b.id === i.businessId,
          );
          if (business) {
            resultData.push({
              issueDate: i.issueDate,
              businessName: business.name,
              product: getProductName(i.lines),
              invoicesPaid: `1 of ${getTotalInvoicesForBusiness(
                i.businessId,
                getInvoicesQuery.data ?? [],
              )}`,
              totalAmount: i.totalAmount,
              status: i.status,
            });
          }
        });
        setFilteredInvoices(resultData);
      }
    }
  }, [
    getInvoicesQuery.data,
    businessesQuery.data,
    usersQuery.data,
    startDate,
    endDate,
  ]);

  const onDateRangeChange = (startDate: Moment, endDate: Moment) => {
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const getInvoiceColumns: () => EuiBasicTableColumn<InvoiceData>[] = () => [
    {
      name: "Date",
      field: "issueDate",
      render: (issueDate: Moment | null) =>
        issueDate ? (
          <EuiText>{moment(issueDate).format("MM/DD/YYYY")}</EuiText>
        ) : (
          <EuiText>Not Issued</EuiText>
        ),
      sortable: true,
    },
    {
      name: "Business Name",
      field: "businessName",
      sortable: true,
    },
    {
      name: "Product",
      field: "product",
      sortable: true,
    },
    {
      name: "Invoices Paid",
      field: "invoicesPaid",
      render: (invoicesPaid: string) => <EuiText>{invoicesPaid}</EuiText>,
    },
    {
      name: "Amount",
      field: "totalAmount",
      render: (totalAmount: number) => (
        <EuiText>{NumberToCurrency(totalAmount)}</EuiText>
      ),
      sortable: true,
    },
    {
      name: "Status",
      field: "status",
      sortable: true,
    },
  ];

  const pagination = {
    initialPageSize: 10,
    pageSizeOptions: [10, 25, 50],
  };

  return (
    <>
      <EuiFlexGroup direction={"row"} justifyContent={"spaceBetween"}>
        <EuiFlexItem>
          <EuiFormRow label={"Date Range"}>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onChange={onDateRangeChange}
              maxDate={maxDate}
              minDate={minDate}
            />
          </EuiFormRow>
        </EuiFlexItem>
        <EuiButton
          onClick={() => {
            DownloadAsCsv(filteredInvoices, "invoices.csv");
          }}
          disabled={filteredInvoices.length === 0}
        >
          Download as CSV
        </EuiButton>
      </EuiFlexGroup>
      <EuiSpacer />
      <EuiFormRow fullWidth label={"Invoices"}>
        <EuiInMemoryTable
          items={filteredInvoices}
          itemId="id"
          columns={getInvoiceColumns()}
          pagination={pagination}
          sorting={true}
        />
      </EuiFormRow>
    </>
  );
};
